<template>
  <Flex
    direction="column"
    v-test="'service-variation-advanced'"
  >
    <BaseHeading>{{ $t('service.bookability') }}</BaseHeading>
    <BaseCard gray>
      <Flex direction="column">
        <BaseCheckbox
          v-model="formData.bookable"
          :label="$t('service.bookable.heading')"
          :description="$t('service.bookable.description')"
          v-test="'service-bookable'"
        />
        <BaseText
          v-if="onlineBookableError"
          color="error"
          size="s"
          v-test="'service-location-bookable-error'"
        >
          {{ $t('service.error_bookable_location') }}
        </BaseText>
        <BaseAlert
          v-if="hasWarningForBooking"
          color="warning"
          size="s"
          :text="
            $t(
              !hasVariationWithDuration
                ? 'service.warning_bookable_no_duration'
                : 'service.warning_bookable_one_no_duration'
            )
          "
          v-test="'service-duration-unbookable'"
        />
      </Flex>
    </BaseCard>
    <BaseCard
      v-if="multiLocation"
      v-show="formData.bookable"
      gray
      v-test="'service-locations'"
    >
      <Flex direction="column">
        <Flex
          direction="column"
          :gap="0.5"
        >
          <BaseText bold>{{ $t('service.locations.heading') }}</BaseText>
          <BaseText size="s">
            {{ $t('service.locations.description') }}
          </BaseText>
        </Flex>
        <LocationSelect
          v-model="formData.bookableLocationIds"
          v-test="'service-locations-list'"
        />
      </Flex>
    </BaseCard>
    <ServiceOffering v-if="unleash.isEnabled('ServiceAvailability')" />
    <BaseHeading>{{ $t('service.communication') }}</BaseHeading>
    <BaseCard gray>
      <Flex direction="column">
        <BaseCheckbox
          v-model="hasExtraInformation"
          :label="$t('service.additional_information.heading')"
          :description="$t('service.additional_information.description')"
          v-test="'service-extra-information-toggle'"
        />
        <BaseEditor
          v-if="hasExtraInformation"
          v-model="formData.extraInformation"
          v-test="'service-extra-information'"
        />
        <BaseAlert
          v-if="hasExtraInformation"
          color="info"
          :text="$t('service.additional_information.alert')"
          :primaryAction="{
            routerLink: { name: 'message-templates' },
            text: $t('messages.nav.email_templates')
          }"
        />
      </Flex>
    </BaseCard>
    <BaseCard
      v-if="rwgEnabled"
      gray
    >
      <BaseInput
        v-model="formData.rwgName"
        :label="$t('service.google_reserve.heading')"
        :info="$t('service.google_reserve.description')"
        v-test="'service-google-reserve'"
      />
    </BaseCard>
    <BaseHeading v-if="company.medical">
      {{ $t('service.other_options') }}
    </BaseHeading>
    <BaseCard
      v-if="company.medical"
      gray
    >
      <Flex direction="column">
        <BaseCheckbox
          v-model="formData.medical"
          :label="$t('service.medical')"
          :description="$t('service.medical_description')"
          v-test="'service-medical-toggle'"
        />
        <BaseInput
          v-if="formData.medical"
          v-model="formData.treatmentCode"
          :label="$t('service.treatment_code')"
          v-test="'service-treatment-code'"
        />
      </Flex>
    </BaseCard>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import LocationSelect from '@/modules/admin/_shared/LocationSelect.vue';
import { useCompanyStore } from '@/stores/company';
import unleash from '@/unleash';
import ServiceOffering from '../availability/ServiceVariationsOffering.vue';
import type { ServiceVariationsFormData } from './types';

const formData = inject<ServiceVariationsFormData>('serviceFormData')!;
const onlineBookableError = inject('onlineBookableError')!;

const { company, multiLocation, getApp } = useCompanyStore();

const rwgEnabled = ref(false);
const hasExtraInformation = ref(false);

const hasVariationWithDuration = computed(() =>
  formData.variations.some((variation) => variation.duration > 0),
);
const hasVariationWithNoDuration = computed(() =>
  formData.variations.some((variation) => variation.duration <= 0),
);
const hasWarningForBooking = computed(
  () => hasVariationWithNoDuration.value || !hasVariationWithDuration.value,
);

getApp('google_reserve').then((app) => {
  rwgEnabled.value = !!app;
});

watch(hasExtraInformation, (value: boolean) => {
  if (!value) {
    formData.extraInformation = '';
  }
});

watch(
  () => formData.extraInformation,
  (value: string) => {
    hasExtraInformation.value = !!value;
  },
  { immediate: true },
);
</script>
